/**
 * Admin app API client. Mirrors the web app's pattern but talks to
 * moderation-service via the X-Test-Admin-Id header in dev (the
 * real Bearer-token flow lands when admin auth UI ships).
 *
 * MODERATION_SERVICE_URL defaults to localhost:8008 for dev.
 */

const MODERATION_BASE = import.meta.env['VITE_MODERATION_SERVICE_URL'] ?? 'http://localhost:8008';
export const BILLING_BASE = import.meta.env['VITE_BILLING_SERVICE_URL'] ?? 'http://localhost:8007';
export const ADMIN_API_BASE = import.meta.env['VITE_ADMIN_API_URL'] ?? 'http://localhost:8010';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`HTTP ${status}`);
    this.name = 'ApiError';
  }
}

export interface ApiRequest {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  url: string;
  body?: unknown;
  baseUrl?: string;
}

/** Dev admin id — picked up from localStorage so each admin's
 *  workstation can identify itself in test mode. Falls back to a
 *  pinned dev account if unset. */
function devAdminId(): string {
  return (
    localStorage.getItem('funkihoo.admin.testAdminId') ?? '00000000-0000-0000-0000-000000000001'
  );
}

export async function apiFetch<T>(req: ApiRequest): Promise<T> {
  const base = req.baseUrl ?? MODERATION_BASE;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // Real-session bearer takes precedence. When present, do NOT
  // attach X-Test-Admin-Id — even in dev — because the server-side
  // admin guard would otherwise prefer the (often-stale fallback)
  // test admin id and 401 with admin_not_found before the Bearer
  // ever gets evaluated. Local dev without sign-in still works:
  // no Bearer → X-Test-Admin-Id rides along as before.
  const bearer =
    typeof localStorage === 'undefined' ? null : localStorage.getItem('funkihoo.admin.bearer');
  if (bearer) {
    headers['Authorization'] = `Bearer ${bearer}`;
  } else {
    headers['X-Test-Admin-Id'] = devAdminId();
  }
  const init: RequestInit = { method: req.method, headers };
  if (req.body !== undefined) init.body = JSON.stringify(req.body);
  const res = await fetch(`${base}${req.url}`, init);
  const text = await res.text();
  const parsed = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) throw new ApiError(res.status, parsed);
  return parsed as T;
}
